import { DesignSystemComponent } from 'designSystem/types';

interface ComponentPreviewProps {
	component: DesignSystemComponent;
	props: Record<string, any>; // Current prop values
	rawCode: {
		tokens: string;
		primitives: string;
		components: string;
	};
}

// Use iframe approach for component preview (similar to StoryPreview)
// This is more reliable than Shadow DOM for custom elements
export const ComponentPreview = ({ component, props, rawCode }: ComponentPreviewProps) => {
	// Build attributes string for the component
	const buildAttributesString = () => {
		return Object.entries(props)
			.filter(([, value]) => value !== undefined && value !== null && value !== '')
			.map(([key, value]) => {
				const prop = component.props.find((p) => p.name === key);
				if (prop?.type === 'json') {
					try {
						// Escape for HTML attribute
						const jsonStr = JSON.stringify(value);
						return `${key}='${jsonStr.replace(/'/g, '&#39;')}'`;
					} catch {
						return '';
					}
				} else if (prop?.type === 'boolean') {
					return value ? key : '';
				} else {
					return `${key}="${String(value).replace(/"/g, '&quot;')}"`;
				}
			})
			.filter(Boolean)
			.join(' ');
	};

	// Check if we have the necessary code
	const hasComponents = rawCode.components && rawCode.components.trim().length > 0;

	// If no components.js, show error state
	if (!hasComponents) {
		return (
			<div
				style={{
					width: '100%',
					height: '100%',
					minHeight: '60px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					padding: '8px',
					fontSize: '10px',
					color: '#f97316',
					textAlign: 'center',
				}}
			>
				No components.js
			</div>
		);
	}

	const attributesStr = buildAttributesString();
	const componentHtml = `<${component.tagName}${attributesStr ? ' ' + attributesStr : ''}></${
		component.tagName
	}>`;

	// Build complete HTML document for iframe
	const fullHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
	<meta charset="UTF-8">
	<meta name="viewport" content="width=device-width, initial-scale=1.0">
	<title>Component Preview</title>
	<style>
		${rawCode.tokens || ''}
	</style>
	<style>
		${rawCode.primitives || ''}
	</style>
	<style>
		/* Preview container styles */
		html, body {
			margin: 0;
			padding: 8px;
			min-height: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
			font-family: var(--ds-font-sans, var(--ds-font-primary, system-ui, sans-serif));
			background: var(--ds-color-bg, #f8fafc);
			color: var(--ds-color-text, #1e293b);
		}
		/* Override fixed/sticky positioning for preview */
		.sidebar, .menu-toggle, .sidebar-mobile-overlay {
			position: relative !important;
			transform: none !important;
		}
		.sidebar-mobile-overlay {
			display: none !important;
		}
		/* Error display */
		.js-error {
			color: #ef4444;
			font-size: 12px;
			padding: 8px;
			background: #fef2f2;
			border-radius: 4px;
			font-family: monospace;
		}
	</style>
</head>
<body>
	${componentHtml}
	<script>
		try {
			${rawCode.components}
		} catch (e) {
			document.body.innerHTML = '<div class="js-error">JS Error: ' + e.message + '</div>';
			console.error('Component script error:', e);
		}
	</script>
</body>
</html>
	`.trim();

	return (
		<iframe
			srcDoc={fullHtml}
			style={{
				width: '100%',
				height: '100%',
				minHeight: '60px',
				maxHeight: '120px',
				border: 'none',
				borderRadius: '4px',
				background: 'transparent',
			}}
			title={`Preview of ${component.name}`}
			sandbox="allow-scripts"
		/>
	);
};
